import type { Notification } from '@aroo/shared';
import { Popover, UnstyledButton } from '@mantine/core';
import { IconBell, IconChevronRight } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../api/notifications';
import { RegText } from '../../components/plate';
import { relativeTime } from '../../features/hr/requestHelpers';
import { useSession } from '../session';
import { useIsMobile } from '../useIsMobile';
import { NotifIcon } from './NotifIcon';
import classes from './shell.module.css';

/** Bell with the unread count; the popover lists notifications (GET /notifications), tap one to open it. */
export function NotificationsPopover() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { signedIn } = useSession();
  const [opened, setOpened] = useState(false);
  const { data } = useNotifications(signedIn);
  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;
  const markAll = useMarkAllNotificationsRead();
  const markOne = useMarkNotificationRead();

  const open = (n: Notification) => {
    if (n.readAt === null) markOne.mutate(n.id);
    setOpened(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={isMobile ? 'calc(100vw - 16px)' : 360}
      position="bottom-end"
      offset={4}
      shadow="md"
      trapFocus
      withinPortal
    >
      <Popover.Target>
        <button
          type="button"
          className={classes.iconButton}
          data-open={opened || undefined}
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
          aria-expanded={opened}
          onClick={() => setOpened((o) => !o)}
        >
          <IconBell size={20} stroke={2} />
          {unread > 0 && (
            <span className={classes.badge} data-tone="danger" aria-hidden="true">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Target>
      <Popover.Dropdown p={0} aria-label="Notifications">
        <div className={classes.popHeader}>
          <span className={classes.popTitle}>Notifications</span>
          <UnstyledButton
            fz={13}
            fw={600}
            c="var(--a-accent-text)"
            p={6}
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            Mark all read
          </UnstyledButton>
        </div>
        <div className={classes.notifList}>
          {items.length === 0 && (
            <div className={classes.notifEmpty}>
              Nothing yet. Requests, decisions and anything that needs you land here.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              className={classes.notifRow}
              data-unread={n.readAt === null || undefined}
              onClick={() => open(n)}
            >
              <span className={classes.notifIcon}>
                <NotifIcon kind={n.kind} />
              </span>
              <span className={classes.notifText}>
                <span className={classes.notifTitle}>
                  <RegText text={n.title} />
                </span>
                {n.body && (
                  <span className={classes.notifBody}>
                    <RegText text={n.body} />
                  </span>
                )}
                <span className={classes.notifWhen}>{relativeTime(n.createdAt)}</span>
              </span>
              <span
                className={classes.unreadDot}
                data-unread={n.readAt === null || undefined}
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
        <Link to="/notifications" className={classes.popFooter} onClick={() => setOpened(false)}>
          See all notifications
          <IconChevronRight size={14} stroke={2} aria-hidden="true" />
        </Link>
      </Popover.Dropdown>
    </Popover>
  );
}
